import { useCallback, useEffect, useState } from 'react'

import type { TranslateFunction } from '@molecule/app-i18n'
import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Tooltip } from '@molecule/app-ui-react'

import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery.js'
import { type EnvSaveResponse, notifyEnvSaveRefusal } from '../utilities/env-save-notice.js'
import { errorKeyOf, requiresSignup, serverErrorMessage } from '../utilities/ops-utilities.js'
import type { EnvScope } from '../utilities/services-utilities.js'
import { Icon } from './Icon.js'
import { MutedNote } from './PanelNotice.js'

/** Which molecule subsystem provides a listed key (never the user's own var). */
type ManagedSource = 'broker' | 'managed'

/**
 * `GET /projects/:id/env?environment=<scope>`.
 *
 * `envVars` is what the REQUESTED environment resolves to (the shared values
 * with that environment's overrides applied), `scopes` breaks the three scopes
 * out separately so a row can say which one it lives in, and molecule-provided
 * rows are listed in `managed` instead of `envVars` — they are not the user's
 * variables and are not editable here.
 *
 * `scopes` / `managed` are optional so an API that predates them parses (and
 * simply yields no scope tags and no managed group) instead of throwing.
 */
interface EnvVarsResponse {
  envVars: Record<string, string>
  count: number
  scopes?: Partial<Record<EnvScope, Record<string, string>>>
  managed?: Record<string, ManagedSource>
  environment?: EnvScope
}

/**
 * Where a listed row's value actually lives, relative to the selected scope.
 *
 * `'override'` — set in this environment; `'shared'` — inherited from the
 * shared scope and only removable there; `'own'` — a shared-tab row (or a
 * response with no `scopes` breakdown to classify by).
 */
type RowScope = 'own' | 'override' | 'shared'

/**
 * Classifies one listed key against the selected scope.
 *
 * The distinction is load-bearing, not cosmetic: deleting a key that is
 * INHERITED from the shared scope while standing on the Production tab would
 * remove it from dev and shared too — the exact cross-scope destruction the
 * per-environment tabs exist to prevent.
 *
 * @param key - The env key.
 * @param scope - The selected environment.
 * @param scopes - The response's per-scope breakdown (absent on older APIs).
 * @returns The row's provenance.
 */
function rowScopeOf(key: string, scope: EnvScope, scopes: EnvVarsResponse['scopes']): RowScope {
  if (scope === 'all') return 'own'
  if (Object.prototype.hasOwnProperty.call(scopes?.[scope] ?? {}, key)) return 'override'
  if (Object.prototype.hasOwnProperty.call(scopes?.all ?? {}, key)) return 'shared'
  return 'own'
}

/** HTTP status off a rejected request (`HttpError.status`, or its response's). */
function httpStatusOf(error: unknown): number | null {
  const shape = error as { status?: unknown; response?: { status?: unknown } } | null | undefined
  if (typeof shape?.status === 'number') return shape.status
  return typeof shape?.response?.status === 'number' ? shape.response.status : null
}

/**
 * One plain sentence for a failed write.
 *
 * The API's deliberate refusals each already say what is wrong with the KEY
 * (reserved by the sandbox, generated by the platform, provided by molecule),
 * so its own sentence wins; `errorKey` supplies a translated sentence for the
 * same classes when the body carries no message. The two 403s are expected
 * states with a next step rather than failures, so they get their own copy.
 *
 * @param error - The caught HTTP error.
 * @param action - Which control failed.
 * @param t - The translate function.
 * @returns The message to render inline.
 */
function writeErrorMessage(
  error: unknown,
  action: 'save' | 'remove',
  t: TranslateFunction,
): string {
  if (requiresSignup(error)) {
    return t('workspace.envVars.error.signup', undefined, {
      defaultValue: 'Create an account to manage environment variables.',
    })
  }
  if (httpStatusOf(error) === 403) {
    return t('workspace.envVars.error.forbidden', undefined, {
      defaultValue: 'You need an admin role to change environment variables.',
    })
  }
  const sentence = serverErrorMessage(error)
  if (sentence) return sentence
  switch (errorKeyOf(error)) {
    case 'envVars.error.blockedKey':
      return t('workspace.envVars.error.blockedKey', undefined, {
        defaultValue: 'That name is reserved by the sandbox. Pick a different one.',
      })
    case 'envVars.error.platformManaged':
      return t('workspace.envVars.error.platformManaged', undefined, {
        defaultValue: "Molecule generates that value for your app, so it can't be set here.",
      })
    case 'envVars.error.moleculeManaged':
      return t('workspace.envVars.error.moleculeManaged', undefined, {
        defaultValue: "Molecule provides that variable, so it can't be removed here.",
      })
    default:
      return action === 'save'
        ? t('workspace.envVars.error.save', undefined, {
            defaultValue: "Couldn't save that variable. Try again.",
          })
        : t('workspace.envVars.error.remove', undefined, {
            defaultValue: "Couldn't remove that variable. Try again.",
          })
  }
}

/**
 * The **Custom environment variables** editor inside the Environment panel.
 *
 * Every read and write carries the panel's selected `scope`, so a value typed
 * on the Production tab is stored as a production override and never reaches
 * the dev sandbox — the editor used to read and write the shared scope only
 * while the surrounding tab said otherwise. Rows inherited from the shared
 * scope are labelled and their delete is disabled, because "remove this
 * variable" from a per-environment tab would take the shared value with it.
 *
 * Molecule-provided rows (broker pointers, managed-storage rows) come back in
 * `managed`, not `envVars`: they render as a read-only group so the user can
 * see them without being offered a delete that breaks brokered AI and is
 * silently undone by the reconciler ~20s later.
 *
 * @param root0 - Component props.
 * @param root0.projectId - The project whose variables these are.
 * @param root0.scope - The environment selected in the panel header.
 * @param root0.locked - Reads work but writes are denied (guest / non-admin).
 * @param root0.onChanged - Called after a successful add or delete.
 * @returns The rendered editor.
 */
export function EnvVarEditor({
  projectId,
  scope,
  locked,
  onChanged,
}: {
  projectId: string
  scope: EnvScope
  locked?: boolean
  onChanged?: () => void
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // 16px input font on phones/touch — iOS zooms any focusable field below 16px.
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')
  const inputFontSize = isMobile || coarse ? 16 : 12
  const [vars, setVars] = useState<Record<string, string>>({})
  const [scopes, setScopes] = useState<EnvVarsResponse['scopes']>(undefined)
  const [managed, setManaged] = useState<Record<string, ManagedSource>>({})
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [writeError, setWriteError] = useState<string | null>(null)

  const loadVars = useCallback(async () => {
    try {
      const res = await http.get<EnvVarsResponse>(`/projects/${projectId}/env?environment=${scope}`)
      setVars(res.data?.envVars ?? {})
      setScopes(res.data?.scopes)
      setManaged(res.data?.managed ?? {})
      setWriteError(null)
    } catch (error) {
      // The panel owns access denial (it loads the vault and passes `locked`),
      // so a failed read here needs no second message.
      console.error('Failed to load env vars', error)
    }
  }, [projectId, http, scope])

  useEffect(() => {
    void loadVars()
  }, [loadVars])

  const handleAdd = async () => {
    const key = newKey.trim()
    const value = newValue
    if (!key) return

    setWriteError(null)
    setSaving(true)
    try {
      // The selected scope travels with the write: a prod value is a prod
      // override, never a shared value the dev sandbox would then be given.
      const res = await http.put<EnvSaveResponse>(`/projects/${projectId}/env`, {
        envVars: { [key]: value },
        environment: scope,
      })
      // Saved, but the asleep app could not be woken to receive it (the
      // owner's running-project limit): say so, with the way out.
      if (scope !== 'prod') notifyEnvSaveRefusal(t, res.data)
      setNewKey('')
      setNewValue('')
      await loadVars()
      onChanged?.()
    } catch (error) {
      // Keep the typed key + value: a failed save must never destroy a pasted
      // secret the user would have to find again.
      setWriteError(writeErrorMessage(error, 'save', t))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (key: string, rowScope: RowScope) => {
    // Belt to the disabled button's braces: an inherited row is removable only
    // where it is set, so nothing here can clear the shared value from a
    // per-environment tab.
    if (rowScope === 'shared') return

    setWriteError(null)
    // No `?environment` on the Shared tab means "remove this variable" — the
    // key, from every scope. On dev/prod only that environment's value goes.
    const query = scope === 'all' ? '' : `?environment=${scope}`
    try {
      const res = await http.delete<EnvSaveResponse>(
        `/projects/${projectId}/env/${encodeURIComponent(key)}${query}`,
      )
      if (scope !== 'prod') notifyEnvSaveRefusal(t, res.data)
      await loadVars()
      onChanged?.()
    } catch (error) {
      setWriteError(writeErrorMessage(error, 'remove', t))
    }
  }

  const entries = Object.entries(vars)
  // A key can hold BOTH a molecule row and the user's own value (a BYO key
  // taken over from the hosted backend). It belongs to the editable list in
  // that case — showing it twice, once with a delete and once without, reads
  // as two different variables.
  const managedEntries = Object.entries(managed).filter(([key]) => !(key in vars))

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
    padding: '4px 0',
    borderBottom: '1px solid var(--color-border)',
  } as const

  const keyStyle = {
    fontFamily: 'monospace',
    fontSize: 12,
    color: 'var(--mol-color-text, #e0e0e0)',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as const

  /** The small muted pill both the scope tags and the Molecule tag render as. */
  const tagStyle = {
    flexShrink: 0,
    fontSize: 10,
    padding: '2px 8px',
    borderRadius: 100,
    background: 'color-mix(in srgb, var(--color-foreground-secondary) 12%, transparent)',
  } as const

  const inputStyle = {
    flex: '1 1 120px',
    minWidth: 0,
    height: isMobile ? 40 : 32,
    boxSizing: 'border-box',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border-secondary)',
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: inputFontSize,
    fontFamily: 'monospace',
    color: 'var(--mol-color-text, #e0e0e0)',
    outline: 'none',
  } as const

  const deleteLabel = t('workspace.envVars.delete', undefined, { defaultValue: 'Delete' })

  // No padding of its own: the editor is embedded in the Environment panel's
  // "Custom environment variables" row, whose expanded body already pads —
  // padding here double-indented the editor against every other row body.
  return (
    <div data-mol-id="env-var-editor" style={{ fontSize: 13 }}>
      {entries.length === 0 && (
        <p className={cm.textMuted} style={{ fontSize: 12 }}>
          {t('workspace.envVars.empty', undefined, { defaultValue: 'No variables set.' })}
        </p>
      )}

      {entries.map(([key, maskedValue]) => {
        const rowScope = rowScopeOf(key, scope, scopes)
        const inherited = rowScope === 'shared'
        return (
          <div key={key} style={rowStyle}>
            <span style={keyStyle}>{key}</span>
            {rowScope !== 'own' && (
              <span
                data-mol-id={`env-var-scope-tag-${key}`}
                className={cm.textMuted}
                style={tagStyle}
              >
                {inherited
                  ? t('workspace.envVars.tag.shared', undefined, { defaultValue: 'shared' })
                  : t('workspace.envVars.tag.override', undefined, { defaultValue: 'override' })}
              </span>
            )}
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: 11,
                color: 'var(--mol-color-text-secondary, #888)',
                maxWidth: 120,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {maskedValue}
            </span>
            <Tooltip
              content={
                inherited
                  ? t('workspace.envVars.deleteShared', undefined, {
                      defaultValue: 'Set on the Shared tab',
                    })
                  : deleteLabel
              }
              placement="left"
            >
              {/* mol-bespoke-button: icon-only × for one env-var row, no label —
                  a dense 14px glyph that must not outweigh the key/value pair it
                  trails; tokens throughout, 40px touch floor on phones. */}
              <button
                type="button"
                data-mol-id={`env-var-delete-${key}`}
                onClick={() => void handleDelete(key, rowScope)}
                disabled={inherited || locked === true}
                className={cm.textError}
                aria-label={deleteLabel}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: 4,
                  cursor: inherited || locked === true ? 'not-allowed' : 'pointer',
                  opacity: inherited || locked === true ? 0.5 : 1,
                  padding: '4px 6px',
                  flexShrink: 0,
                  // Touch floor for the dense-row icon button.
                  ...(isMobile ? { minWidth: 40, minHeight: 40 } : {}),
                }}
              >
                <Icon name="x-mark" size={14} />
              </button>
            </Tooltip>
          </div>
        )
      })}

      {/* Molecule-provided rows: shown so nothing looks missing, read-only so
          nobody is offered a delete that breaks brokered AI (and that the
          reconciler undoes ~20s later, making the delete look broken). */}
      {managedEntries.length > 0 && (
        <div data-mol-id="env-vars-managed" style={{ marginTop: 12 }}>
          {managedEntries.map(([key]) => (
            <div key={key} data-mol-id={`env-var-managed-${key}`} style={rowStyle}>
              <span style={keyStyle}>{key}</span>
              <span className={cm.textMuted} style={tagStyle}>
                {t('workspace.setup.brokerTag', undefined, { defaultValue: 'Molecule' })}
              </span>
            </div>
          ))}
          <MutedNote molId="env-vars-managed-note" style={{ marginBottom: 0 }}>
            {t('workspace.envVars.managedNote', undefined, {
              defaultValue: 'Provided by Molecule — nothing to do.',
            })}
          </MutedNote>
        </div>
      )}

      {/* Add new — wraps only when the row can't fit (phones), where each
          input then takes its own full-width line. */}
      <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
        <input
          type="text"
          data-mol-id="env-var-key-input"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
          placeholder={t('workspace.envVars.keyPlaceholder', undefined, { defaultValue: 'KEY' })}
          disabled={locked === true}
          style={inputStyle}
        />
        <input
          type="text"
          data-mol-id="env-var-value-input"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder={t('workspace.envVars.valuePlaceholder', undefined, {
            defaultValue: 'value',
          })}
          disabled={locked === true}
          style={inputStyle}
        />
        <Button
          size="sm"
          variant="outline"
          data-mol-id="env-var-add"
          disabled={locked === true || saving || !newKey.trim()}
          onClick={() => void handleAdd()}
          // Matches inputStyle's height at BOTH breakpoints — a fixed 32 left
          // this button 8px short of the 40px inputs it shares the row with on
          // phones/touch.
          style={{ flexShrink: 0, height: isMobile ? 40 : 32 }}
        >
          {t('workspace.envVars.add', undefined, { defaultValue: 'Add' })}
        </Button>
      </div>

      {writeError !== null && (
        <p
          data-mol-id="env-var-editor-error"
          role="alert"
          className={cm.textError}
          style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}
        >
          {writeError}
        </p>
      )}

      {locked === true && (
        <MutedNote molId="env-var-editor-locked" style={{ marginTop: 8, marginBottom: 0 }}>
          {t('workspace.envVars.locked', undefined, {
            defaultValue: 'You can see these but not change them.',
          })}
        </MutedNote>
      )}
    </div>
  )
}
